import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  Calendar, MapPin, Clock, User, LogOut, Search, 
  ChevronRight, X, CheckCircle, MessageCircle, Navigation, Map,
  Filter, Target, Bell, BellOff, Heart, Shield, Shirt
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import WhatsAppShareButton from '../components/WhatsAppShareButton';
import { SessionsMap } from '../components/LeafletMap';
import { FavoritesBadge } from '../components/FavoriteButton';
import SessionSidePanel from '../components/SessionSidePanel';
import LevelFilter, { getSessionCountsByLevel } from '../components/LevelFilter';
import DateFilter, { getSessionCountsByDate, matchesDateFilter } from '../components/DateFilter';
import { HeaderLogo } from '../components/AnimatedLogo';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  all: '🎯', yoga: '🧘', renforcement: '💪', mobilite: '🤸',
  outdoor: '🏃', cardio: '❤️', pilates: '🎯', boxe: '🥊', danse: '💃',
};

const CATEGORY_IDS = ['all', 'yoga', 'renforcement', 'mobilite', 'outdoor', 'cardio', 'pilates', 'boxe', 'danse'];

const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

export default function UserDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t, lang } = useTranslation();
  
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nearbySessions, setNearbySessions] = useState([]);
  const [userLocation, setUserLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(true);
  const [showMap, setShowMap] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [maxRadius, setMaxRadius] = useState(2);
  const [showFilters, setShowFilters] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [quickLevelFilter, setQuickLevelFilter] = useState('all');
  const [quickDateFilter, setQuickDateFilter] = useState('all');

  useEffect(() => {
    fetchBookings();
    getUserLocation();
    checkNotificationPermission();
  }, []);

  useEffect(() => {
    if (userLocation) {
      fetchNearbySessions();
    }
  }, [userLocation]);

  const checkNotificationPermission = () => {
    if ('Notification' in window) {
      setNotificationsEnabled(Notification.permission === 'granted');
    }
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error(t('dashboard.toastNotifNotSupported'));
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        toast.success(t('dashboard.toastNotifEnabled'));
        
        if ('serviceWorker' in navigator) {
          const registration = await navigator.serviceWorker.register('/sw.js');
          console.log('Service Worker registered:', registration);
          
          new Notification('Motivya', {
            body: t('dashboard.notifBody'),
            icon: '/logo.png',
            badge: '/logo.png'
          });
        }
      } else {
        toast.error(t('dashboard.toastNotifDenied'));
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      toast.error(t('dashboard.toastNotifError'));
    }
  };

  const disableNotifications = () => {
    setNotificationsEnabled(false);
    toast.info(t('dashboard.toastNotifDisabled'));
  };

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude });
          setLocationLoading(false);
        },
        () => {
          setUserLocation({ lat: 50.8503, lng: 4.3517 });
          setLocationLoading(false);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setUserLocation({ lat: 50.8503, lng: 4.3517 });
      setLocationLoading(false);
    }
  };

  const fetchNearbySessions = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sessions?page_size=500`);
      if (response.ok) {
        const data = await response.json();
        // Handle paginated response
        const sessions = data.items || data;
        const futureSessions = sessions.filter(s => new Date(s.date) >= new Date());
        setNearbySessions(futureSessions);
      }
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const filteredSessions = useMemo(() => {
    if (!userLocation) return nearbySessions;
    
    return nearbySessions.filter(session => {
      if (selectedCategory !== 'all' && session.category !== selectedCategory) return false;
      if (quickLevelFilter !== 'all' && session.level !== quickLevelFilter && session.level !== 'tous') return false;
      if (quickDateFilter !== 'all' && !matchesDateFilter(session.date, quickDateFilter)) return false;
      const distance = calculateDistance(userLocation.lat, userLocation.lng, session.latitude, session.longitude);
      return distance <= maxRadius;
    });
  }, [nearbySessions, selectedCategory, maxRadius, userLocation, quickLevelFilter, quickDateFilter]);

  const levelCounts = useMemo(() => {
    const preFiltered = nearbySessions.filter(session => {
      if (selectedCategory !== 'all' && session.category !== selectedCategory) return false;
      if (quickDateFilter !== 'all' && !matchesDateFilter(session.date, quickDateFilter)) return false;
      if (!userLocation) return true;
      const distance = calculateDistance(userLocation.lat, userLocation.lng, session.latitude, session.longitude);
      return distance <= maxRadius;
    });
    return getSessionCountsByLevel(preFiltered);
  }, [nearbySessions, selectedCategory, maxRadius, userLocation, quickDateFilter]);

  const dateCounts = useMemo(() => {
    const preFiltered = nearbySessions.filter(session => {
      if (selectedCategory !== 'all' && session.category !== selectedCategory) return false;
      if (quickLevelFilter !== 'all' && session.level !== quickLevelFilter && session.level !== 'tous') return false;
      if (!userLocation) return true;
      const distance = calculateDistance(userLocation.lat, userLocation.lng, session.latitude, session.longitude);
      return distance <= maxRadius;
    });
    return getSessionCountsByDate(preFiltered);
  }, [nearbySessions, selectedCategory, maxRadius, userLocation, quickLevelFilter]);

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/users/bookings`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setBookings(data);
      }
    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (bookingId) => {
    if (!confirm(t('dashboard.confirmCancel'))) return;

    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        toast.success(t('dashboard.toastCancelled'));
        fetchBookings();
      } else {
        const error = await response.json();
        toast.error(error.detail || t('dashboard.toastCancelError'));
      }
    } catch (error) {
      toast.error(t('dashboard.toastCancelError'));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const locale = lang === 'nl' ? 'nl-BE' : lang === 'en' ? 'en-GB' : 'fr-FR';
    return date.toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const upcomingBookings = bookings.filter(b => 
    b.status === 'confirmed' && b.session && new Date(b.session.date) >= new Date()
  );
  const pastBookings = bookings.filter(b => b.session && new Date(b.session.date) < new Date());
  const cancelledBookings = bookings.filter(b => b.status === 'cancelled');

  const handleSessionSelect = (session) => setSelectedSession(session);
  const handleBookFromPanel = (sessionId) => navigate(`/session/${sessionId}?book=true`);
  const closeSidePanel = () => setSelectedSession(null);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <HeaderLogo linkTo="/" size="sm" testId="dashboard-logo" />
            
            <div className="flex items-center gap-4">
              <Link to="/explore">
                <Button variant="ghost" size="sm" data-testid="nav-explore">
                  <Search className="w-4 h-4 mr-2" />
                  {t('dashboard.explore')}
                </Button>
              </Link>
              <Link to="/favorites" className="relative">
                <Button variant="ghost" size="sm" data-testid="nav-favorites">
                  <Heart className="w-4 h-4 mr-2" />
                  {t('dashboard.favorites')}
                </Button>
                <FavoritesBadge />
              </Link>
              <Link to="/outfit">
                <Button variant="ghost" size="sm" data-testid="nav-outfit" className="text-emerald-400">
                  <Shirt className="w-4 h-4 mr-2" />
                  {t('nav.outfit') || 'Tenue'}
                </Button>
              </Link>
              <Link to="/admin/coach-applications">
                <Button variant="ghost" size="sm" data-testid="nav-admin" className="text-primary">
                  <Shield className="w-4 h-4 mr-2" />
                  {t('dashboard.admin')}
                </Button>
              </Link>
              <Link to="/profile">
                <Button variant="ghost" size="sm" data-testid="nav-profile">
                  <User className="w-4 h-4 mr-2" />
                  {t('dashboard.profile')}
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="nav-logout">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-3xl font-bold mb-2" data-testid="dashboard-welcome">
            {t('dashboard.hello')} <span className="gradient-text">{user?.name?.split(' ')[0]}</span>
          </h1>
          <p className="text-muted-foreground">{t('dashboard.subtitle')}</p>
        </div>

        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          <Link to="/explore" className="bg-card border border-border rounded-lg p-6 card-hover group">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg mb-1">{t('dashboard.findActivity')}</h3>
                <p className="text-muted-foreground text-sm">{t('dashboard.findActivityDesc')}</p>
              </div>
              <ChevronRight className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
          </Link>
          
          <Link to="/favorites" className="bg-card border border-border rounded-lg p-6 card-hover group">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Heart className="w-5 h-5 text-red-500" />
                  <h3 className="font-bold text-lg">{t('dashboard.myFavorites')}</h3>
                </div>
                <p className="text-muted-foreground text-sm">{t('dashboard.savedSessions')}</p>
              </div>
              <ChevronRight className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
          </Link>
          
          <Link to="/profile" className="bg-card border border-border rounded-lg p-6 card-hover group">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-lg mb-1">{t('dashboard.myProfile')}</h3>
                <p className="text-muted-foreground text-sm">{t('dashboard.myProfileDesc')}</p>
              </div>
              <ChevronRight className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors" />
            </div>
          </Link>
        </div>

        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-lg sm:text-xl font-bold uppercase">{t('dashboard.nearMe')}</h2>
              {userLocation && (
                <span className="flex items-center gap-1 text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded-full">
                  <Navigation className="w-3 h-3" />
                  {t('dashboard.geolocated')}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={notificationsEnabled ? disableNotifications : requestNotificationPermission}
                className={notificationsEnabled ? "text-primary" : "text-muted-foreground"}
                data-testid="toggle-notifications-btn"
              >
                {notificationsEnabled ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
              </Button>
              <Button
                variant={showFilters ? "default" : "ghost"}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? "bg-primary text-primary-foreground" : "text-muted-foreground"}
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{t('dashboard.filters')}</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowMap(!showMap)}
                className="text-muted-foreground"
                data-testid="toggle-map-btn"
              >
                <Map className="w-4 h-4 mr-1 sm:mr-2" />
                <span className="hidden sm:inline">{showMap ? t('dashboard.hide') : t('dashboard.show')}</span>
              </Button>
            </div>
          </div>

          {showFilters && (
            <div className="bg-card border border-border rounded-xl p-4 mb-4 animate-fade-in" data-testid="filters-panel">
              <div className="mb-4">
                <label className="text-sm font-medium text-muted-foreground mb-2 block">{t('dashboard.category')}</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_IDS.map(catId => (
                    <button
                      key={catId}
                      onClick={() => setSelectedCategory(catId)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        selectedCategory === catId
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted/50 text-muted-foreground hover:bg-muted'
                      }`}
                      data-testid={`category-filter-${catId}`}
                    >
                      <span className="mr-1">{CATEGORY_ICONS[catId]}</span>
                      {t(`categories.${catId}`)}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  {t('dashboard.searchRadius')} <span className="text-primary font-bold">{maxRadius} km</span>
                </label>
                <div className="flex items-center gap-4">
                  <input
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.5"
                    value={maxRadius}
                    onChange={(e) => setMaxRadius(parseFloat(e.target.value))}
                    className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                    data-testid="radius-slider"
                  />
                  <div className="flex gap-1 text-xs text-muted-foreground">
                    <span className={maxRadius >= 0.5 ? 'text-primary' : ''}>0.5</span>
                    <span>-</span>
                    <span className={maxRadius >= 1 ? 'text-primary' : ''}>1</span>
                    <span>-</span>
                    <span className={maxRadius >= 1.5 ? 'text-primary' : ''}>1.5</span>
                    <span>-</span>
                    <span className={maxRadius >= 2 ? 'text-primary' : ''}>2km</span>
                  </div>
                </div>
              </div>

              <div className="mt-4 pt-3 border-t border-border flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  <span className="text-primary font-bold">{filteredSessions.length}</span> {t('dashboard.sessionsFound')}
                  {selectedCategory !== 'all' && (
                    <span> {t('dashboard.inCategory')} {t(`categories.${selectedCategory}`)}</span>
                  )}
                  <span> {t('dashboard.withinRadius')} {maxRadius}km</span>
                </span>
                {(selectedCategory !== 'all' || maxRadius !== 2) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { setSelectedCategory('all'); setMaxRadius(2); }}
                    className="text-xs"
                  >
                    {t('dashboard.reset')}
                  </Button>
                )}
              </div>
            </div>
          )}

          {showMap && (
            <div className="bg-card border border-border rounded-xl overflow-hidden animate-fade-in" data-testid="user-dashboard-map">
              {locationLoading ? (
                <div className="h-[300px] sm:h-[400px] flex items-center justify-center bg-gray-100/5">
                  <div className="text-center">
                    <div className="spinner mb-3"></div>
                    <p className="text-muted-foreground text-sm">{t('dashboard.locating')}</p>
                  </div>
                </div>
              ) : (
                <div className="h-[300px] sm:h-[400px] relative">
                  <div className="absolute top-3 left-3 z-[1000] flex flex-col gap-2">
                    <LevelFilter
                      selectedLevel={quickLevelFilter}
                      onLevelChange={setQuickLevelFilter}
                      counts={levelCounts}
                      variant="inline"
                      className="bg-white/95 backdrop-blur-sm rounded-full shadow-lg border border-gray-200 p-1"
                    />
                    <DateFilter
                      selectedDate={quickDateFilter}
                      onDateChange={setQuickDateFilter}
                      counts={dateCounts}
                    />
                  </div>
                  
                  <SessionsMap
                    sessions={filteredSessions}
                    onSessionSelect={handleSessionSelect}
                    showUserLocation={true}
                    showRadius={true}
                    radiusKm={maxRadius}
                    mapStyle="light"
                    center={userLocation ? [userLocation.lat, userLocation.lng] : [50.8503, 4.3517]}
                    zoom={maxRadius <= 1 ? 15 : 14}
                    className="h-full"
                  />
                  
                  {selectedSession && (
                    <SessionSidePanel 
                      session={selectedSession}
                      onClose={closeSidePanel}
                      onBook={handleBookFromPanel}
                    />
                  )}
                </div>
              )}
              
              <div className="p-3 sm:p-4 border-t border-border bg-background/50">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-center gap-3 sm:gap-4 text-xs sm:text-sm text-muted-foreground flex-wrap">
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-yellow-500"></span>
                      {t('dashboard.youAreHere')}
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-primary"></span>
                      {filteredSessions.length} {t('dashboard.sessionsAt')} {maxRadius}km
                    </span>
                  </div>
                  <Link to="/explore">
                    <Button variant="outline" size="sm" data-testid="see-all-sessions-btn">
                      {t('dashboard.viewAll')}
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="mb-8">
          <h2 className="text-xl font-bold mb-4 uppercase">{t('dashboard.upcomingSessions')}</h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : upcomingBookings.length === 0 ? (
            <div className="bg-card border border-border rounded-lg p-8 text-center">
              <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">{t('dashboard.noUpcoming')}</h3>
              <p className="text-muted-foreground mb-4">{t('dashboard.findNext')}</p>
              <Link to="/explore">
                <Button className="btn-primary" data-testid="find-activity-btn">
                  {t('dashboard.exploreSessions')}
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-4">
              {upcomingBookings.map((booking) => {
                const spotsLeft = booking.session ? 
                  booking.session.max_participants - booking.session.current_participants : 0;
                
                return (
                  <div 
                    key={booking.booking_id}
                    className="bg-card border border-border rounded-lg p-5 card-hover"
                    data-testid={`booking-${booking.booking_id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="badge-confirmed">
                            <CheckCircle className="w-3 h-3 inline mr-1" />
                            {t('dashboard.confirmed')}
                          </span>
                          {booking.session?.status === 'confirmed' && (
                            <span className="text-xs text-green-400">{t('dashboard.sessionValidated')}</span>
                          )}
                          {spotsLeft > 0 && (
                            <span className="text-xs text-yellow-400">
                              {spotsLeft} {spotsLeft > 1 ? t('dashboard.spotsLeft') : t('dashboard.spotLeft')}
                            </span>
                          )}
                        </div>
                        <h3 className="font-bold text-lg">{booking.session?.title}</h3>
                        <div className="flex flex-wrap gap-4 mt-2 text-sm text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {formatDate(booking.session?.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {booking.session?.start_time}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {booking.session?.location}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {spotsLeft > 0 && booking.session && (
                          <WhatsAppShareButton session={booking.session} variant="icon" />
                        )}
                        <Link to={`/session/${booking.session_id}`}>
                          <Button variant="outline" size="sm" data-testid={`view-session-${booking.booking_id}`}>
                            {t('dashboard.viewDetails')}
                          </Button>
                        </Link>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleCancelBooking(booking.booking_id)}
                          data-testid={`cancel-booking-${booking.booking_id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {pastBookings.length > 0 && (
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4 uppercase text-muted-foreground">{t('dashboard.history')}</h2>
            <div className="space-y-3">
              {pastBookings.slice(0, 5).map((booking) => (
                <div 
                  key={booking.booking_id}
                  className="bg-card/50 border border-border rounded-lg p-4 opacity-70"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold">{booking.session?.title}</h3>
                      <p className="text-sm text-muted-foreground">
                        {formatDate(booking.session?.date)} &bull; {booking.session?.start_time}
                      </p>
                    </div>
                    <span className="text-muted-foreground text-sm">{booking.amount}&euro;</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {cancelledBookings.length > 0 && (
          <div>
            <h2 className="text-xl font-bold mb-4 uppercase text-muted-foreground">{t('dashboard.cancelled')}</h2>
            <div className="space-y-3">
              {cancelledBookings.slice(0, 3).map((booking) => (
                <div 
                  key={booking.booking_id}
                  className="bg-card/50 border border-border rounded-lg p-4 opacity-50"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold line-through">{booking.session?.title || t('dashboard.cancelledSession')}</h3>
                      <p className="text-sm text-muted-foreground">{t('dashboard.refunded')}</p>
                    </div>
                    <span className="badge-cancelled">{t('dashboard.cancelledBadge')}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
